import type { FC, ReactNode } from 'react'

import Image from 'next/image'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { ScriptHeading } from '@/components/generals/script-heading'
import { Chip } from '@/components/generals/chip'
import { Choose } from '@/components/home/choose'
import { serviceItems } from '@/constants/services'

const Services: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Amenities, beaches and boat launch'
                description='Charcoal or gas grills with every cabin, a small craft boat launch, beaches, fire pits by the water, lawn games and walking trails on Lake Superior.'
            />

            <PageHero
                lead='Amen'
                tail='ities'
                intro='Charcoal or gas grills with every cabin, a small craft boat launch on site, beaches for swimming, fire pits by the water and large fields for many games.'
            />

            <section className='relative mb-4 h-[46vh] min-h-[320px] overflow-hidden md:h-[62vh]'>
                <div className='absolute left-0 top-0 h-full w-full' data-parallax='30'>
                    <Image
                        src='/images/lake-superior-shoreline.jpg'
                        alt='Sand and stone beach below the wooded bluff on Lake Superior'
                        fill
                        sizes='100vw'
                        className='object-cover'
                        priority
                    />
                </div>
            </section>

            <ScriptHeading lead='Kick back on our' tail='spacious grounds' />

            <section className='shell pb-20 md:pb-28 lg:pb-32'>
                <h2 className='t-h1 text-center' data-reveal='up'>
                    <span className='block'>
                        Explore
                    </span>

                    <em className='block'>
                        Our amenities
                    </em>
                </h2>

                <div className='mt-14 flex flex-col gap-6'>
                    {serviceItems.map((item) => (
                        <article
                            key={item.index}
                            id={item.id}
                            className='grid scroll-mt-28 gap-8 rounded-[10px] border border-hairline bg-cream-soft p-6 md:grid-cols-2 md:items-center md:gap-12 md:p-9'
                            data-reveal='up'
                        >
                            <div className='relative aspect-[4/3] overflow-hidden rounded-[8px] bg-cream-deep'>
                                <div className='absolute left-0 top-0 h-full w-full' data-parallax='15'>
                                    <Image
                                        src={item.image}
                                        alt={item.alt}
                                        fill
                                        sizes='(max-width: 809px) 100vw, 45vw'
                                        className='object-cover'
                                    />
                                </div>
                            </div>

                            <div>
                                <span className='font-heading text-[72px] font-medium leading-[0.9] tracking-[-0.04em] text-brand md:text-[96px]'>
                                    {item.index}
                                </span>

                                <h3 className='t-h2 mt-4'>
                                    {item.title}
                                </h3>

                                <div className='mt-5 flex flex-wrap gap-2'>
                                    {item.chips.map((chip) => (
                                        <Chip key={chip} tone='outline' className='bg-white'>
                                            {chip}
                                        </Chip>
                                    ))}
                                </div>

                                <p className='t-h5 mt-6 text-ink-soft'>
                                    {item.description}
                                </p>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <Choose />
        </Layout>
    )
}

export default Services
